/**
 * "Sua IDE em casa está ligada?" (docs/refactor/16-ide-em-casa.md) — in the web's header, beside the
 * live connection: whether the person's desktop app is open, and whether it takes what is sent from
 * here. The app gives a sign of life at least every five minutes; with none in six, it is "sem sinal há 2 h" —
 * off, asleep or offline, which Vephus cannot tell apart, so it does not pretend to. Leads to Suas máquinas, where each computer is told apart. Not drawn in the desktop app: there,
 * the app is the IDE.
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Monitor } from 'lucide-react'
import { api } from '../lib/api'
import { isDesktop } from '../lib/desktop'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import type { DeveloperMachine } from '../lib/types'

export type HomePcState = { kind: 'on'; name: string | null } | { kind: 'idle'; name: string | null } | { kind: 'off'; at: string } | null

/** What the header says, from the person's computers: the best of them. Null with no computer at all. */
export function homePcState(machines: DeveloperMachine[] | undefined): HomePcState {
  if (!machines?.length) return null
  const on = machines.filter((m) => m.online)
  const taking = on.find((m) => m.takingCommands)
  if (taking) return { kind: 'on', name: on.length === 1 ? taking.name : null }
  if (on.length) return { kind: 'idle', name: on.length === 1 ? on[0]!.name : null }
  const last = [...machines].sort((a, b) => Date.parse(b.lastSeenAt) - Date.parse(a.lastSeenAt))[0]!
  return { kind: 'off', at: last.lastSeenAt }
}

export function HomePcStatus({ enabled }: { enabled: boolean }) {
  const { t, locale } = useI18n()
  const { data: machines } = useQuery({ queryKey: ['machines'], queryFn: api.promptQueue.machines, enabled: enabled && !isDesktop(), refetchInterval: 60_000, retry: false })
  if (isDesktop() || !enabled) return null
  const state = homePcState(machines)
  if (!state) return null
  const text = state.kind === 'on'
    ? (state.name ? t('homePc.onNamed', { name: state.name }) : t('homePc.on'))
    : state.kind === 'idle'
      ? t('homePc.idle')
      // Honest (docs/refactor/28): Vephus cannot tell off from asleep or offline — only how long it has been quiet.
      : t('homePc.off', { time: formatRelative(state.at, locale) })
  return (
    <Link to="/agents/remote" className={`topbar__homepc is-${state.kind}`} title={t(`homePc.hint.${state.kind}`)}>
      <Monitor size={13} aria-hidden /> <span>{text}</span>
    </Link>
  )
}
